// InvitesPage.tsx — Active invites list with mint + revoke modals.

import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useFirestoreQuery, invalidateCache } from '@/hooks/useFirestoreQuery';
import { getLicenseOrg } from '@/services/firestore/licenseOrgs';
import { listActiveInvites } from '@/services/firestore/invites';
import { inviteLicenseOrgMember } from '@/services/functions/inviteLicenseOrgMember';
import { revokeLicenseOrgInvite } from '@/services/functions/revokeLicenseOrgInvite';
import { Breadcrumb } from '@/components/Breadcrumb';
import { SkeletonList } from '@/components/SkeletonRow';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Modal } from '@/components/Modal';
import { fmtDate, fmtExpiresIn } from '@/utils/format';
import type { LicenseOrg, LicenseOrgInvite } from '@/types/domain';

interface InvitesPayload {
  org: LicenseOrg | null;
  invites: LicenseOrgInvite[];
}

export function InvitesPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const cacheKey = orgId ? `invites:list:${orgId}` : null;

  const fetcher = useCallback(async (): Promise<InvitesPayload> => {
    if (!orgId) return { org: null, invites: [] };
    const [org, invites] = await Promise.all([
      getLicenseOrg(orgId),
      listActiveInvites(orgId),
    ]);
    return { org, invites };
  }, [orgId]);

  const { data, loading, error, refetch } = useFirestoreQuery(cacheKey, fetcher);

  const [mintOpen, setMintOpen] = useState(false);
  const [revokeInviteId, setRevokeInviteId] = useState<string | null>(null);

  if (!orgId) return <ErrorState message="Missing org id in URL" />;
  if (loading && !data) return <SkeletonList count={5} />;
  if (error) return <ErrorState message={error.message} />;
  if (!data?.org) {
    return <ErrorState message={`License org "${orgId}" not found`} />;
  }

  const orgName = data.org.name || orgId;
  const orgPath = `/license-orgs/${encodeURIComponent(orgId)}`;
  const invalidate = () => {
    invalidateCache(`invites:list:${orgId}`);
    invalidateCache(`licenseOrg:detail:${orgId}`);
    refetch();
  };

  return (
    <>
      <Breadcrumb
        crumbs={[
          { label: 'License Orgs', to: '/license-orgs' },
          { label: orgName, to: orgPath },
          'Invites',
        ]}
      />
      <header className="console-page-header">
        <div>
          <h1 className="console-page-title">Invites</h1>
          <div className="console-page-subtitle">
            {data.invites.length} active invite{data.invites.length === 1 ? '' : 's'} (max 20)
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link className="console-btn console-btn-ghost" to={`${orgPath}/members`}>Members</Link>
          <Link className="console-btn console-btn-ghost" to={`${orgPath}/audit`}>Audit log</Link>
          <button
            className="console-btn console-btn-primary"
            type="button"
            onClick={() => setMintOpen(true)}
          >
            <span className="material-icons">add_link</span> Mint invite link
          </button>
        </div>
      </header>

      {data.invites.length === 0 ? (
        <EmptyState
          icon="mail_outline"
          title="No active invites"
          body="Mint a single-use invite link with the button above."
        />
      ) : (
        <InvitesTable invites={data.invites} onRevoke={setRevokeInviteId} />
      )}

      {mintOpen && (
        <MintModal
          orgId={orgId}
          onClose={() => setMintOpen(false)}
          onSuccess={invalidate}
        />
      )}
      {revokeInviteId && (
        <RevokeModal
          inviteId={revokeInviteId}
          onClose={() => setRevokeInviteId(null)}
          onSuccess={invalidate}
        />
      )}
    </>
  );
}

function InvitesTable({
  invites,
  onRevoke,
}: {
  invites: LicenseOrgInvite[];
  onRevoke: (id: string) => void;
}) {
  return (
    <div className="console-table-wrap">
      <table className="console-table">
        <thead>
          <tr>
            <th>Invite</th>
            <th>Created</th>
            <th>Expires</th>
            <th>Uses</th>
            <th style={{ textAlign: 'right' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {invites.map((inv) => {
            const expiresLabel = fmtExpiresIn(inv.expiresAt);
            const expiresClass = expiresLabel === 'expired' ? 'is-suspended' : 'is-active';
            return (
              <tr key={inv.id}>
                <td>
                  <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: 'var(--text-secondary)' }}>
                    {inv.id.slice(0, 12)}…
                  </code>
                  <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 2 }}>
                    {inv.productId}
                  </div>
                </td>
                <td>{fmtDate(inv.createdAt)}</td>
                <td>
                  <span className={`console-status ${expiresClass}`}>{expiresLabel}</span>
                  <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 2 }}>
                    {fmtDate(inv.expiresAt)}
                  </div>
                </td>
                <td>{inv.usedCount} / {inv.maxUses}</td>
                <td className="actions">
                  <button
                    className="console-btn console-btn-danger"
                    type="button"
                    onClick={() => onRevoke(inv.id)}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MintModal({
  orgId,
  onClose,
  onSuccess,
}: {
  orgId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [productId, setProductId] = useState('theme_pack');
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reveal, setReveal] = useState<{ acceptUrl: string; expiresAt: string } | null>(null);

  async function handleMint() {
    if (submitting) return;
    if (!productId.trim()) {
      setErrorMessage('Product id is required');
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const res = await inviteLicenseOrgMember({
        licenseOrgId: orgId,
        productId: productId.trim(),
        expiresInDays,
        note: note.trim() || undefined,
      });
      setReveal({ acceptUrl: res.acceptUrl, expiresAt: res.expiresAt });
      onSuccess();
    } catch (err) {
      setErrorMessage(
        (err as { message?: string })?.message ?? 'Could not mint invite',
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (reveal) {
    return <RevealAcceptUrlModal {...reveal} onClose={onClose} />;
  }

  return (
    <Modal open onClose={onClose} ariaLabel="Mint invite link">
      <div className="console-modal-title">Mint an invite link</div>
      <div className="console-modal-description">
        Single-use link. The first person who opens it on a device with SocialMesh
        installed and taps Join is allocated a seat.
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <label className="console-form-label" htmlFor="mintProduct">Product</label>
          <input
            id="mintProduct"
            className="console-form-input"
            type="text"
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            maxLength={64}
          />
        </div>
        <div style={{ width: 120 }}>
          <label className="console-form-label" htmlFor="mintExpiry">Expires</label>
          <select
            id="mintExpiry"
            className="console-form-input"
            value={String(expiresInDays)}
            onChange={(e) => setExpiresInDays(parseInt(e.target.value, 10))}
          >
            <option value="1">1 day</option>
            <option value="3">3 days</option>
            <option value="7">7 days</option>
            <option value="14">14 days</option>
            <option value="30">30 days</option>
          </select>
        </div>
      </div>
      <div className="console-form-field">
        <label className="console-form-label" htmlFor="mintNote">Note (optional)</label>
        <input
          id="mintNote"
          className="console-form-input"
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={64}
          placeholder="e.g. for alice@team.com"
        />
      </div>
      {errorMessage && <div className="console-modal-error" role="alert">{errorMessage}</div>}
      <div className="console-modal-actions">
        <button className="console-btn console-btn-ghost" type="button" onClick={onClose}>Cancel</button>
        <button
          className="console-btn console-btn-primary"
          type="button"
          onClick={handleMint}
          disabled={submitting}
        >
          {submitting ? 'Minting…' : 'Mint link'}
        </button>
      </div>
    </Modal>
  );
}

function RevealAcceptUrlModal({
  acceptUrl,
  expiresAt,
  onClose,
}: {
  acceptUrl: string;
  expiresAt: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  let expiresLabel = '';
  try {
    expiresLabel = ` Expires ${new Date(expiresAt).toISOString().slice(0, 10)}.`;
  } catch {
    // ignore malformed
  }
  return (
    <Modal open onClose={onClose} ariaLabel="Invite minted">
      <div className="console-modal-title">Invite minted. Share the link.</div>
      <div className="console-modal-description">
        This URL is single-use and shown <strong>once</strong>. Send it to the
        person you want to join the org.{expiresLabel}
      </div>
      <div className="console-code-reveal">
        <span className="console-code-reveal-label">Accept URL</span>
        <span className="console-code-reveal-value" style={{ fontSize: 13 }}>{acceptUrl}</span>
        <span className="console-code-reveal-warning">
          The link is also no longer retrievable from the server after this dialog closes.
        </span>
      </div>
      <div className="console-modal-actions">
        <button
          className="console-btn console-btn-ghost"
          type="button"
          onClick={() => {
            if (navigator.clipboard?.writeText) {
              navigator.clipboard.writeText(acceptUrl).catch(() => {});
              setCopied(true);
            }
          }}
        >
          {copied ? 'Copied' : 'Copy URL'}
        </button>
        <button className="console-btn console-btn-primary" type="button" onClick={onClose}>Done</button>
      </div>
    </Modal>
  );
}

function RevokeModal({
  inviteId,
  onClose,
  onSuccess,
}: {
  inviteId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleConfirm() {
    if (submitting) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await revokeLicenseOrgInvite({ inviteId });
      onSuccess();
      onClose();
    } catch (err) {
      setErrorMessage(
        (err as { message?: string })?.message ?? 'Could not revoke invite',
      );
      setSubmitting(false);
    }
  }

  return (
    <Modal open onClose={onClose} ariaLabel="Revoke invite">
      <div className="console-modal-title">Revoke this invite?</div>
      <div className="console-modal-description">
        The link will stop working immediately. Anyone holding a copy will get a "cancelled" landing page when they tap Open.
      </div>
      <div className="console-modal-meta">
        <div>
          <strong>Invite</strong><br />
          <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>
            {inviteId.slice(0, 16)}…
          </code>
        </div>
      </div>
      {errorMessage && <div className="console-modal-error" role="alert">{errorMessage}</div>}
      <div className="console-modal-actions">
        <button className="console-btn console-btn-ghost" type="button" onClick={onClose}>Cancel</button>
        <button
          className="console-btn console-btn-danger"
          type="button"
          onClick={handleConfirm}
          disabled={submitting}
        >
          {submitting ? 'Revoking…' : 'Revoke'}
        </button>
      </div>
    </Modal>
  );
}
